import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../contexts/ToastContext';
import { useThemeClasses } from '../lib/themes/utils';
import { API_ENDPOINTS, getAuthHeaders } from '../lib/config/api-endpoints';
import Input from './common/Input';
import { ROUTES } from '../src/constants/routes';

interface IntakeEmailEntryProps {
  onSuccess?: () => void;
}

const IntakeEmailEntry: React.FC<IntakeEmailEntryProps> = ({ onSuccess }) => {
  const navigate = useNavigate();
  const { showError, showSuccess } = useToast();
  const theme = useThemeClasses();
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateEmail(email)) {
      showError('Please enter a valid email address');
      return;
    }

    setIsLoading(true);

    try {
      // First, create or get the client
      const createResponse = await fetch(API_ENDPOINTS.clients.createOrGet, {
        method: 'POST',
        headers: getAuthHeaders(API_ENDPOINTS.clients.createOrGet),
        body: JSON.stringify({ email: email.toLowerCase() })
      });

      if (!createResponse.ok) {
        const error = await createResponse.json();
        throw new Error(error.message || 'Failed to process email');
      }

      // const clientResponse = await createResponse.json(); // TODO: Use for validation
      // const clientData = clientResponse.data || clientResponse; // TODO: Use if needed for validation

      // Then send magic link using unified system with autoSignup for intake
      const magicLinkResponse = await fetch(API_ENDPOINTS.auth.magicLinkRequest, {
        method: 'POST',
        headers: getAuthHeaders(API_ENDPOINTS.auth.magicLinkRequest),
        body: JSON.stringify({ 
          email: email.toLowerCase(),
          callbackURL: '/intake-form',
          autoSignup: true  // Enable auto-signup for intake form users
        })
      });

      const magicLinkData = await magicLinkResponse.json();
      
      if (!magicLinkResponse.ok) {
        throw new Error(magicLinkData.error || 'Failed to send magic link');
      }
      
      // Handle local development auto-redirect
      const isLocalDev = import.meta.env.DEV || window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
      
      if (isLocalDev && magicLinkData.magic_link) {
        console.log('🔧 LOCAL DEV: Auto-redirecting to magic link', magicLinkData.magic_link);
        // Small delay to show the sending state, then redirect
        setTimeout(() => {
          window.location.href = magicLinkData.magic_link;
        }, 500);
        return;
      }

      setEmailSent(true);
      showSuccess('Check your email for a secure link to continue');
      
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      console.error('Email submission error:', error);
      showError(error instanceof Error ? error.message : 'Failed to send magic link');
    } finally {
      setIsLoading(false);
    }
  };

  if (emailSent) {
    return (
      <div className={`min-h-screen ${theme.backgrounds.gradient} flex items-center justify-center p-6`}>
        <div className={`max-w-md w-full ${theme.cards.elevated} p-8 text-center animate-fadeIn`}>
          <div className="mb-6">
            <div className={`mx-auto w-20 h-20 ${theme.backgrounds.gradientPrimary} ${theme.radius.full} flex items-center justify-center mb-4`}>
              <svg className="h-10 w-10 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </div>
          </div>
          <h2 className={`text-2xl ${theme.typography.heading} mb-4`}>Check Your Email</h2>
          <p className={`${theme.text.neutral} mb-6`}>
            We've sent a secure link to <strong className={theme.text.primary}>{email}</strong>
          </p>
          <p className={`text-sm ${theme.text.neutralLight} mb-4`}>
            Click the link in your email to continue with your wellness assessment.
          </p>
          <div className={`${theme.cards.simple} p-4 mb-6`}>
            <div className="flex items-center justify-center text-sm">
              <svg className="w-4 h-4 mr-2 text-warning-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className={theme.text.neutralLight}>Link expires in 24 hours</span>
            </div>
          </div>
          <button
            onClick={() => {
              setEmailSent(false);
              setEmail('');
            }}
            className={`${theme.buttons.outline} px-6 py-2`}
          >
            Use a different email
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${theme.backgrounds.gradient} flex items-center justify-center p-6`}>
      <div className={`max-w-lg w-full ${theme.cards.elevated} p-8 sm:p-10 animate-fadeIn`}>
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 mb-4 px-4 py-2 bg-primary-100/50 backdrop-blur-sm rounded-full">
            <svg className="w-5 h-5 text-primary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="text-primary-600 text-sm font-medium">Wellness Assessment</span>
          </div>
          
          <h1 className={`text-3xl sm:text-4xl ${theme.typography.headingLarge} mb-3`}>
            Begin Your Journey
          </h1>
          <p className={`text-lg ${theme.text.neutral} leading-relaxed`}>
            Start your personalized wellness assessment to unlock healing insights
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <Input
              label="Enter your email to begin"
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              required
            />
            <div className="mt-3 flex items-center gap-2 text-sm">
              <svg className="w-4 h-4 text-success-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
              <span className={theme.text.neutralLight}>
                We'll send you a secure link to access your assessment
              </span>
            </div>
          </div>

          <button
            type="submit"
            disabled={isLoading || !email}
            className={`
              group w-full px-6 py-4 
              ${theme.backgrounds.gradientPrimary} text-white 
              ${theme.radius.large} font-semibold text-lg
              hover:scale-105 hover:${theme.shadows.large}
              transition-all ${theme.animations.duration}
              disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100
              ${theme.shadows.medium}
              relative overflow-hidden
            `}
          >
            <div className="relative z-10 flex items-center justify-center gap-3">
              {isLoading ? (
                <>
                  <svg className="animate-spin h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
                  </svg>
                  Sending secure link...
                </>
              ) : (
                <>
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                  Start My Wellness Assessment
                  <svg className="w-5 h-5 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </>
              )}
            </div>
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
          </button>
        </form>

        {/* Information Card */}
        <div className={`mt-8 ${theme.cards.simple} p-6`}>
          <h3 className={`text-sm font-semibold ${theme.text.neutralDark} mb-3 flex items-center gap-2`}>
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            What happens next?
          </h3>
          <ul className={`text-sm ${theme.text.neutralLight} space-y-2`}>
            <li className="flex items-start gap-2">
              <svg className="w-4 h-4 mt-0.5 text-primary-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              Save your progress and return anytime
            </li>
            <li className="flex items-start gap-2">
              <svg className="w-4 h-4 mt-0.5 text-primary-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              Receive personalized wellness insights
            </li>
            <li className="flex items-start gap-2">
              <svg className="w-4 h-4 mt-0.5 text-primary-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              Book follow-up sessions when ready
            </li>
          </ul>
        </div>

        {/* Footer */}
        <div className="mt-6 text-center">
          <p className={`text-sm ${theme.text.neutralLight}`}>
            Already started your assessment?{' '}
            <button
              type="button"
              onClick={() => navigate(ROUTES.PORTAL.LOGIN)}
              className={`${theme.text.primary} hover:${theme.text.primaryDark} font-medium transition-colors`}
            >
              Continue where you left off
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

export default IntakeEmailEntry;